import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import useEntrySound from "../../../hooks/useEntrySound";
import useIdleNudge from "../../../hooks/useIdleNudge";
import LearningWorldMap from "./LearningWorldMap";
import Skeleton from "../../../components/Kids/Skeleton";
import EmptyState from "../../../components/Kids/EmptyState";
import SoundLangControls from "../../../components/Kids/SoundLangControls";
import GuestSignupWall from "../../../components/Kids/GuestSignupWall";

// Icon (Material Symbols name) per subject — falls back to `sports_esports`
// for admin-added subjects the dashboard doesn't recognise by key.
const SUBJECT_ICONS = { math: "calculate", english: "menu_book", science: "biotech", art: "palette", coding: "code" };
// Per-subject "shelf" accent — solid color (not the card gradient) used for
// the shelf's progress bar and icon chip.
const SUBJECT_ACCENT = { math: "#a83300", english: "#805200", science: "#006782", art: "#cd4813", coding: "#a06900" };

const GUEST_FREE_GAMES = 2;

const API   = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");

// Fallback subject list — used only if the /subjects endpoint is unreachable,
// so the hub never blocks on it. Admin-added subjects come from the API.
const FALLBACK_SUBJECTS = [
  { key: "math",    label: "Math",    emoji: "🔢", color: "#5B7FB9" },
  { key: "english", label: "English", emoji: "📖", color: "#7E5BC0" },
  { key: "science", label: "Science", emoji: "🔭", color: "#2A946A" },
  { key: "art",     label: "Art",     emoji: "🎨", color: "#D1618E" },
  { key: "coding",  label: "Coding",  emoji: "💻", color: "#B4790C" },
];

const GAME_EMOJIS = {
  "count-and-match":    "🔢",
  "number-ninja":       "🥷",
  "word-builder":       "🔤",
  "story-scramble":     "📚",
  "science-explorer":   "🔭",
  "code-blocks-jr":     "💻",
  "alphabet-adventure": "🔤",
  "math-planet":        "🪐",
  "vehicle-explorer":   "🚗",
  "vehicle-quiz":       "🚙",
  "vehicle-speller":    "🚌",
  "vehicle-sort":       "🚛",
  "food-explorer":      "🍎",
  "food-quiz":          "🍊",
  "food-speller":       "🥕",
  "food-sort":          "🧺",
  "urdu-tahaji":        "ا",
  "letter-match":       "🔤",
  "animal-homes":       "🏡",
  "smoothie-stand":     "🥤",
};

// Difficulty dots, not stars — stars next to a kids' game read as "you
// scored this," not "this is how hard it is." Dots avoid that confusion.
const DIFF_STARS = { easy: "🟢 Easy", medium: "🟡 Medium", hard: "🔴 Hard" };
// How many of the 3 difficulty dots are "lit" (filled) per level.
const DIFF_DOTS = { easy: 1, medium: 2, hard: 3 };

const GAME_ROUTES = {
  quiz:          (slug, id, child) => `/kids/games/quiz/${slug}?gameId=${id}&childId=${child}`,
  drag_drop:     (slug, id, child) => `/kids/games/drag/${slug}?gameId=${id}&childId=${child}`,
  typing:        (slug, id, child) => `/kids/games/typing/${slug}?gameId=${id}&childId=${child}`,
  flashcard:     (slug, id, child) => `/kids/games/flashcard/${slug}?gameId=${id}&childId=${child}`,
  tracing:       (slug, id, child) => `/kids/games/tracing/${slug}?gameId=${id}&childId=${child}`,
  puzzle:        (slug, id, child) => `/kids/games/puzzle/${slug}?gameId=${id}&childId=${child}`,
  memory:        (slug, id, child) => `/kids/games/memory/${slug}?gameId=${id}&childId=${child}`,
  connect_lines: (slug, id, child) => `/kids/games/connect/${slug}?gameId=${id}&childId=${child}`,
  smoothie:      (slug, id, child) => `/kids/games/smoothie/${slug}?gameId=${id}&childId=${child}`,
  story:         () => null,
};

const DIFF_ORDER = { easy: 0, medium: 1, hard: 2 };
const PLAYABLE_TYPES = ["quiz", "drag_drop", "typing", "flashcard", "tracing", "puzzle", "memory", "connect_lines", "smoothie"];
const isPlayable = (g) => PLAYABLE_TYPES.includes(g.game_type);
const isDone = (g) =>
  g.completed === true || g.is_completed === true ||
  g.best_score != null  || (g.progress && g.progress.completed === true);

const TYPE_LABELS = { drag_drop: "Sort", connect_lines: "Connect" };
// Material Symbols per game type, used by the card's icon chip.
const TYPE_SYMBOLS = { quiz: "quiz", drag_drop: "swap_horiz", typing: "keyboard", flashcard: "style", tracing: "draw", memory: "psychology", puzzle: "extension", connect_lines: "link", smoothie: "local_bar" };

export default function KidsGameHub() {
  const { t }     = useTranslation();
  const navigate  = useNavigate();
  const [params]  = useSearchParams();

  // Only the child ID comes from the URL — everything else is fetched by ID.
  const childId = params.get("childId") || sessionStorage.getItem("childId");

  // Instant paint from cache, then refresh authoritative data by ID.
  const [profile, setProfile] = useState(() => ({
    name:        sessionStorage.getItem("childName")   || "Star",
    grade:       parseInt(sessionStorage.getItem("childGrade")  || "1"),
    total_xp:    parseInt(sessionStorage.getItem("childXP")     || "0"),
    coins:       parseInt(sessionStorage.getItem("childCoins")  || "0"),
    streak_days: parseInt(sessionStorage.getItem("childStreak") || "0"),
  }));

  const [aiEnabled, setAiEnabled] = useState(true); // gated by the family's plan

  const childName  = profile.name;
  // First name only for on-screen greetings — a full "Firstname Lastname"
  // reads formal/impersonal for a Grade-1 kid, and is friendlier here (the
  // full name is still what's stored and used everywhere else, e.g. reports).
  const displayName = childName.split(" ")[0];
  const grade      = profile.grade;
  const childXP    = profile.total_xp;
  const childCoins = profile.coins;
  const streak     = profile.streak_days;

  useEntrySound(t("voice.welcome", { name: childName }));
  useIdleNudge(childName); // gently wakes the child by name after inactivity

  // Securely fetch the child's profile by ID (no name / XP exposed in the URL).
  useEffect(() => {
    if (!childId) return;
    axios.get(`${API}/api/kids/child/${childId}/profile`, {
      headers: { Authorization: `Bearer ${token()}` },
    }).then((res) => {
      const c = res.data?.data;
      if (!c) return;
      setProfile({
        name: c.name, grade: c.grade ?? 1,
        total_xp: c.total_xp ?? 0, coins: c.coins ?? 0, streak_days: c.streak_days ?? 0,
      });
      setAiEnabled(c.ai_enabled !== false);
      sessionStorage.setItem("childId",     c.id);
      sessionStorage.setItem("childName",   c.name);
      sessionStorage.setItem("childGrade",  c.grade ?? 1);
      sessionStorage.setItem("childXP",     c.total_xp ?? 0);
      sessionStorage.setItem("childCoins",  c.coins ?? 0);
      sessionStorage.setItem("childStreak", c.streak_days ?? 0);
    }).catch(() => { /* keep cached values */ });
  }, [childId]);

  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [subjects, setSubjects] = useState(FALLBACK_SUBJECTS);

  useEffect(() => {
    axios.get(`${API}/api/kids/subjects`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => { const s = r.data?.data; if (Array.isArray(s) && s.length) setSubjects(s); })
      .catch(() => { /* keep fallback list */ });
  }, []);

  const [games,  setGames]  = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoad]  = useState(true);
  const [showSignupWall, setShowSignupWall] = useState(false);
  // Grid is the default landing view; persisted across remounts (e.g.
  // returning from a game) so the child's last choice sticks instead of
  // reverting to the map every time.
  const [view, setViewState] = useState(() => sessionStorage.getItem("kids_hub_view") || "grid");
  const setView = useCallback((v) => {
    setViewState(v);
    sessionStorage.setItem("kids_hub_view", v);
  }, []);
  const [drawerOpen, setDrawerOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/kids/games`, {
        // child_id lets the backend scope out school/class-made games to
        // just this child's own school/class — without it, every school's
        // content is visible to every kid on the platform (see
        // KidsGame::scopeVisibleTo).
        params:  { grade, child_id: childId },
        headers: { Authorization: `Bearer ${token()}` },
      });
      setGames(res.data?.data || []);
    } catch {
      // show fallback demo games if API unavailable
      setGames(DEMO_GAMES);
    } finally {
      setLoad(false);
    }
  }, [grade, childId]);

  useEffect(() => { load(); }, [load]);

  // Daily Challenge fetch
  useEffect(() => {
    axios.get(`${API}/api/kids/daily-challenge`, {
      params: { grade },
      headers: { Authorization: `Bearer ${token()}` },
    }).then(r => setDailyChallenge(r.data?.data)).catch(() => {});
  }, [grade]);

  const visible = games.filter(g => filter === "all" || g.subject === filter);
  const level   = Math.floor(childXP / 100) + 1;
  const xpBar   = ((childXP % 100) / 100) * 100;
  const xpToNext = 100 - (childXP % 100);

  // Build the learning journey: order stops by grade, then difficulty, then id
  const journey = [...visible].sort((a, b) =>
    (a.min_grade - b.min_grade) ||
    ((DIFF_ORDER[a.difficulty] ?? 1) - (DIFF_ORDER[b.difficulty] ?? 1)) ||
    (a.id - b.id)
  );
  // Current position = first playable stop the child hasn't completed yet
  const currentIdx = (() => {
    const i = journey.findIndex(g => isPlayable(g) && !isDone(g));
    return i === -1 ? journey.length - 1 : i;
  })();
  const currentStop = journey[currentIdx];

  const handleLogout = () => {
    axios.post(`${API}/api/kids/child/logout`, {}, { headers: { Authorization: `Bearer ${token()}` } })
      .catch(() => {})
      .finally(() => {
        localStorage.removeItem("kids_parent_token");
        localStorage.removeItem("kids_active_mode");
        localStorage.removeItem("kids_child");
        sessionStorage.removeItem("kids_is_guest");
        sessionStorage.removeItem("kids_guest_games_played");
        navigate("/kids/kid-login");
      });
  };

  const handlePlay = (game) => {
    if (sessionStorage.getItem("kids_is_guest") === "1") {
      const played = Number(sessionStorage.getItem("kids_guest_games_played") || 0);
      if (played >= GUEST_FREE_GAMES) {
        setShowSignupWall(true);
        return;
      }
      sessionStorage.setItem("kids_guest_games_played", String(played + 1));
    }
    const routeFn = GAME_ROUTES[game.game_type];
    if (!routeFn) {
      return showComingSoon(game.name);
    }
    const path = routeFn(game.slug, game.id, childId);
    if (!path) {
      showComingSoon(game.name);
    } else {
      navigate(path);
    }
  };

  const showComingSoon = (name) => {
    alert(`${name} is coming very soon! 🚀 Keep playing other games to earn XP!`);
  };

  // Immersive Learning World map (default kid game screen)
  if (view === "map" && !loading && journey.length > 0) {
    return (
      <LearningWorldMap
        childId={childId} childName={childName} aiEnabled={aiEnabled}
        coins={childCoins} xp={childXP} level={level} streak={streak} grade={grade}
        journey={journey} currentIdx={currentIdx}
        onPlay={handlePlay} navigate={navigate} onGrid={() => setView("grid")} />
    );
  }

  return (
    <div className="min-h-screen bg-surface-bright text-on-surface font-kidsbody relative">
      <style>{`
        .game-card:hover .play-btn{transform:scale(1.05)}
        .game-card:hover{transform:translateY(-4px)}
        .game-card{transition:transform 0.2s ease}
        .play-btn{transition:transform 0.15s ease}
        .hide-scrollbar::-webkit-scrollbar{display:none}
        .hide-scrollbar{-ms-overflow-style:none;scrollbar-width:none}
      `}</style>

      {/* Ambient background glow */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(ellipse at top right, #ffddb4, transparent 50%), radial-gradient(ellipse at bottom left, #ffdbd0, transparent 50%)" }}>
        <div className="absolute top-[10%] end-[15%] w-24 h-24 rounded-full bg-secondary-fixed/30 blur-2xl kids-shape-float" />
        <div className="absolute bottom-[20%] start-[30%] w-32 h-32 rounded-full bg-primary-fixed/40 blur-3xl kids-shape-float-delayed" />
      </div>

      {/* ── Sidebar (persistent glass rail on desktop, slide-out drawer on mobile) ── */}
      {drawerOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setDrawerOpen(false)} />}
      <aside className={`fixed start-0 top-0 bottom-0 w-[280px] bg-white lg:bg-white/40 lg:backdrop-blur-md border-e border-glass-border shadow-md z-50 flex flex-col py-6 px-4 transition-transform duration-300 ease-in-out overflow-y-auto lg:translate-x-0 ${drawerOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"}`}
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 24px)" }}>
        <div className="flex items-center gap-3 mb-8 px-1">
          <div className="w-12 h-12 rounded-xl bg-primary-container flex items-center justify-center shrink-0 shadow-inner">
            <span className="text-2xl">🧸</span>
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-headline-md text-xl text-primary truncate">Cabil Kids</h1>
            <p className="font-label-lg text-[11px] text-on-surface-variant opacity-80">Playful Intelligence</p>
          </div>
          <button onClick={() => setDrawerOpen(false)}
            className="w-8 h-8 rounded-full bg-surface-container hover:bg-surface-container-high flex items-center justify-center text-on-surface-variant text-sm font-bold lg:hidden shrink-0">✕</button>
        </div>

        <nav className="flex flex-col gap-1.5 flex-grow">
          <SidebarLink icon="home" label={t("kids.games.hub.title")} active={view === "grid"} onClick={() => { setView("grid"); setDrawerOpen(false); }} />
          <SidebarLink icon="map" label="Adventure Map" active={view === "map"} onClick={() => { setView("map"); setDrawerOpen(false); }} />
          <SidebarLink icon="swords" label="Quiz Battle" onClick={() => { navigate(`/kids/multiplayer?childId=${childId}`); setDrawerOpen(false); }} />
          <SidebarLink icon="play_circle" label="Watch Zone" onClick={() => { navigate(`/kids/watch?childId=${childId}`); setDrawerOpen(false); }} />
          {aiEnabled && (
            <SidebarLink icon="smart_toy" label="AI Buddy" onClick={() => { navigate(`/kids/ai-chat?childId=${childId}`); setDrawerOpen(false); }} />
          )}
          <SidebarLink icon="view_in_ar" label="Point & Learn AR" onClick={() => { navigate(`/kids/ar?childId=${childId}`); setDrawerOpen(false); }} />
          <SidebarLink icon="emoji_events" label="Leaderboard" onClick={() => { navigate(`/kids/leaderboard?childId=${childId}`); setDrawerOpen(false); }} />
          <SidebarLink icon="military_tech" label="Achievements" onClick={() => { navigate(`/kids/achievements?childId=${childId}`); setDrawerOpen(false); }} />
          <SidebarLink icon="bar_chart" label="Reports" onClick={() => { navigate(`/kids/reports`); setDrawerOpen(false); }} />

          <div className="mt-auto pt-4 border-t border-glass-border">
            <SidebarLink icon="family_restroom" label="For grown-ups" onClick={() => { navigate("/kids/parent/dashboard"); setDrawerOpen(false); }} />
            <SidebarLink icon="logout" label="Log out" danger onClick={handleLogout} />
          </div>
        </nav>
      </aside>

      {/* ── Main content ── */}
      <div className="lg:ms-[280px] relative z-10 min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-canvas-base/90 backdrop-blur-xl border-b border-glass-border shadow-sm">
          <div className="max-w-[1440px] mx-auto flex flex-wrap items-center justify-between gap-3 px-4 md:px-gutter py-3 md:h-20 md:py-0">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setDrawerOpen(true)}
                className="lg:hidden shrink-0 w-10 h-10 rounded-full bg-surface-container flex items-center justify-center text-on-surface">
                <span className="material-symbols-outlined">menu</span>
              </button>
              <div className="w-10 h-10 rounded-full bg-surface-container hidden sm:flex items-center justify-center border-2 border-primary-fixed shrink-0">
                <span className="material-symbols-outlined text-primary">waving_hand</span>
              </div>
              <h2 className="font-headline-md text-lg md:text-xl text-on-surface truncate">Hi, {displayName}!</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 shrink-0">
              <div className="hidden sm:flex items-center gap-2 bg-surface-container-high px-3 py-1.5 rounded-full border border-surface-variant">
                <div className="w-7 h-7 rounded-full bg-secondary text-on-secondary flex items-center justify-center font-label-lg text-[11px]">L{level}</div>
                <div className="w-16 h-2 bg-surface-variant rounded-full overflow-hidden">
                  <div className="h-full bg-secondary rounded-full" style={{ width: `${xpBar}%` }} />
                </div>
              </div>
              {streak > 0 && (
                <div className="flex items-center gap-1.5 bg-surface-container-high px-3 py-1.5 rounded-full border border-surface-variant">
                  <span className="material-symbols-outlined icon-filled text-primary-container text-lg">local_fire_department</span>
                  <span className="font-label-lg text-[13px] text-on-surface">{streak}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 bg-surface-container-high px-3 py-1.5 rounded-full border border-surface-variant">
                <span className="material-symbols-outlined icon-filled text-tertiary-fixed-dim text-lg">monetization_on</span>
                <span className="font-label-lg text-[13px] text-on-surface">{childCoins}</span>
              </div>
              <div className="border-s border-glass-border ps-2 md:ps-3">
                <SoundLangControls theme="light" />
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-gutter max-w-[1440px] mx-auto pt-6 pb-16 flex flex-col gap-8">
          {/* View toggle + subject filter row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex gap-1.5 rounded-full p-1.5 w-full sm:w-[230px] bg-surface-container">
              <button onClick={() => setView("grid")}
                className="flex-1 text-center py-2.5 rounded-full font-headline-md text-sm transition"
                style={view === "grid" ? { background: "#a83300", color: "#fff", boxShadow: "0 4px 10px rgba(168,51,0,.3)" } : { color: "#8d7168" }}>
                🎮 {t("kids.games.hub.allGames")}
              </button>
              <button onClick={() => setView("map")}
                className="flex-1 text-center py-2.5 rounded-full font-headline-md text-sm transition" style={{ color: "#8d7168" }}>
                🗺️ Map
              </button>
            </div>

            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1 sm:pb-0">
              <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>🎮 All</FilterChip>
              {subjects.map((s) => (
                <FilterChip key={s.key} active={filter === s.key} onClick={() => setFilter(s.key)} accent={s.color}>
                  {s.emoji} {s.label}
                </FilterChip>
              ))}
            </div>
          </div>

          {/* Hero: Daily Challenge, or a "Next Adventure" nudge when there's no
              challenge configured for today */}
          {dailyChallenge?.game ? (
            <HeroBanner
              eyebrow="Daily Challenge" badge="2× XP" badgeIcon="bolt"
              title={dailyChallenge.game.name} emoji={GAME_EMOJIS[dailyChallenge.game.slug] || "🎮"}
              subtitle="A fresh challenge, picked just for you today."
              ctaLabel={t("kids.games.hub.play")} onCta={() => handlePlay(dailyChallenge.game)} />
          ) : (!loading && currentStop) ? (
            <HeroBanner
              eyebrow="Next Adventure" emoji={GAME_EMOJIS[currentStop.slug] || "🎯"}
              title={isPlayable(currentStop) ? currentStop.name : "Keep exploring your trail!"}
              subtitle={`${xpToNext} XP to reach Level ${level + 1}`}
              ctaLabel={isPlayable(currentStop) ? "Go" : null} onCta={() => handlePlay(currentStop)} />
          ) : null}

          {/* Content */}
          {loading ? (
            <HubSkeleton />
          ) : visible.length === 0 ? (
            <EmptyGamesState filter={filter} onShowAll={() => setFilter("all")} />
          ) : view === "map" ? (
            <AdventureTrail journey={journey} currentIdx={currentIdx} onPlay={handlePlay} childName={childName} />
          ) : filter === "all" ? (
            <SubjectShelves games={visible} subjects={subjects} onPlay={handlePlay} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-3.5">
              {visible.map((game) => (
                <GameCard key={game.id} game={game} onPlay={handlePlay} />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Drawer toggle (floating, when drawer is closed — mobile only). */}
      {!drawerOpen && (
        <button onClick={() => setDrawerOpen(true)}
          style={{ bottom: "calc(78px + env(safe-area-inset-bottom) + 16px)" }}
          className="fixed start-4 z-30 w-12 h-12 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition lg:hidden">
          <span className="material-symbols-outlined">menu</span>
        </button>
      )}

      {showSignupWall && <GuestSignupWall onClose={() => setShowSignupWall(false)} />}
    </div>
  );
}

/* ── Sidebar nav link — active state gets a right-border accent + tinted bg,
   matching the Stitch dashboard mockup's tab styling. ── */
function SidebarLink({ icon, label, active, danger, onClick }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-label-lg text-[13px] transition-transform duration-150 active:scale-95 text-start group ${
        active ? "text-primary border-e-4 border-primary bg-surface-container-high/40"
        : danger ? "text-danger hover:bg-danger/10"
        : "text-on-surface-variant hover:bg-surface-container-high/50"
      }`}>
      <span className={`material-symbols-outlined text-xl group-hover:scale-110 transition-transform ${active ? "icon-filled" : ""}`}>{icon}</span>
      {label}
    </button>
  );
}

/* ── Subject filter pill — brand-filled when active, otherwise outlined in
   the subject's own accent color. ── */
function FilterChip({ active, accent, onClick, children }) {
  return (
    <button onClick={onClick}
      className="shrink-0 rounded-full px-4 py-2 text-sm font-label-lg border transition"
      style={active
        ? { background: "#a83300", borderColor: "#a83300", color: "#fff" }
        : { background: "#fff", borderColor: "#e1bfb5", color: accent || "#6A47E0" }}>
      {children}
    </button>
  );
}

/* ── Hero banner — Daily Challenge (or the Next Adventure fallback), styled
   as the mockup's big rust-orange card with a decorative glow and a
   tactile "pressable" CTA button. ── */
function HeroBanner({ eyebrow, badge, badgeIcon, title, subtitle, emoji, ctaLabel, onCta }) {
  return (
    <section className="relative w-full rounded-xl overflow-hidden shadow-md flex items-center justify-between gap-6 p-6 md:p-10 bg-primary">
      <div className="absolute -end-20 -top-20 w-96 h-96 bg-primary-fixed-dim rounded-full blur-3xl opacity-40" />
      <div className="relative z-10 flex flex-col gap-3 md:gap-4 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          {badge && (
            <span className="px-3 py-1 bg-tertiary-fixed text-on-tertiary-fixed font-label-lg text-[11px] rounded-full uppercase tracking-wider shadow-sm flex items-center gap-1">
              {badgeIcon && <span className="material-symbols-outlined text-sm icon-filled">{badgeIcon}</span>} {badge}
            </span>
          )}
          <span className="text-primary-fixed font-label-lg text-[12px] uppercase tracking-wide">{eyebrow}</span>
        </div>
        <h2 className="font-headline-lg-mobile md:font-headline-lg text-headline-lg-mobile md:text-headline-lg text-on-primary drop-shadow-md truncate">{title}</h2>
        {subtitle && <p className="font-body-md text-primary-fixed max-w-lg">{subtitle}</p>}
        {ctaLabel && (
          <button onClick={onCta}
            className="mt-1 bg-on-primary text-primary font-label-lg text-sm px-6 md:px-8 py-3 md:py-4 rounded-lg shadow-sm border-b-4 border-surface-variant hover:translate-y-[2px] hover:border-b-2 active:translate-y-[4px] active:border-b-0 transition-all self-start flex items-center gap-2">
            <span className="material-symbols-outlined icon-filled">play_arrow</span> {ctaLabel}
          </button>
        )}
      </div>
      {emoji && <div className="relative z-10 text-6xl md:text-8xl shrink-0">{emoji}</div>}
    </section>
  );
}

/* ── AdaptedMind-style subject shelves: one row per subject, each with its
   own progress bar and a horizontally-scrollable strip of game cards. Any
   game whose subject isn't in the known list still gets a shelf (with a
   generic identity) rather than silently disappearing. ── */
function SubjectShelves({ games, subjects, onPlay }) {
  const bySubject = {};
  games.forEach((g) => {
    const key = g.subject || "general";
    (bySubject[key] = bySubject[key] || []).push(g);
  });

  const known = subjects.map((s) => s.key);
  const extraKeys = Object.keys(bySubject).filter((k) => !known.includes(k));
  const shelves = [
    ...subjects.filter((s) => bySubject[s.key]?.length),
    ...extraKeys.map((k) => ({ key: k, label: k, emoji: "🎮", color: "#6A47E0" })),
  ];

  return (
    <div className="flex flex-col gap-10">
      {shelves.map((s) => {
        const list = bySubject[s.key] || [];
        const done = list.filter(isDone).length;
        const accent = SUBJECT_ACCENT[s.key] || s.color || "#a83300";
        return (
          <section key={s.key}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="flex items-center gap-2 font-headline-md text-xl text-on-surface">
                <span className="material-symbols-outlined" style={{ color: accent }}>{SUBJECT_ICONS[s.key] || "extension"}</span>
                {s.label}
              </h2>
              <span className="font-label-lg text-[12px] text-on-surface-variant shrink-0">{done}/{list.length} done</span>
            </div>
            <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-1 px-1">
              {list.map((game) => (
                <GameCard key={game.id} game={game} onPlay={onPlay} />
              ))}
              <SeeAllCard accent={accent} onClick={() => {}} />
            </div>
          </section>
        );
      })}
    </div>
  );
}

/* ── Single game card — fixed 300×340 tile matching the Stitch mockup:
   icon chip, title/meta, difficulty dots, reward pills, circular CTA. ── */
function GameCard({ game, onPlay }) {
  const { t } = useTranslation();
  const isComingSoon = !isPlayable(game);
  const done = isDone(game);
  const emoji = GAME_EMOJIS[game.slug] || "🎮";
  const accent = SUBJECT_ACCENT[game.subject] || "#a83300";
  const dots = DIFF_DOTS[game.difficulty] || 1;

  return (
    <div className="game-card relative w-[220px] sm:w-[260px] shrink-0 h-[300px] sm:h-[320px] rounded-xl bg-white border border-surface-variant shadow-sm hover:shadow-md flex flex-col overflow-hidden">
      {isComingSoon && (
        <div className="absolute inset-0 bg-white/85 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="text-center">
            <span className="material-symbols-outlined text-3xl text-outline">lock</span>
            <p className="text-xs font-label-lg text-outline mt-1">{t("kids.games.hub.locked")}</p>
          </div>
        </div>
      )}

      {done && (
        <div className="absolute top-3 end-3 bg-secondary text-on-secondary text-[10px] font-label-lg px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1 z-10">
          <span className="material-symbols-outlined icon-filled text-xs">check_circle</span> {t("kids.games.hub.completed")}
        </div>
      )}

      <div className="h-[120px] flex items-center justify-center shrink-0" style={{ background: `${accent}1A` }}>
        <span className="text-5xl">{emoji}</span>
      </div>

      <div className="flex-1 flex flex-col p-4 gap-2 min-w-0">
        <h3 className="font-headline-md text-[15px] text-on-surface leading-tight"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {game.name}
        </h3>
        <p className="flex items-center gap-1 font-body-md text-[12px] text-on-surface-variant truncate">
          <span className="material-symbols-outlined text-sm">{TYPE_SYMBOLS[game.game_type] || "sports_esports"}</span>
          {TYPE_LABELS[game.game_type] || game.game_type} · Gr {game.min_grade === 0 ? "K" : game.min_grade}–{game.max_grade}
        </p>

        <div className="flex items-center gap-1 mt-0.5">
          {[1, 2, 3].map((i) => (
            <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: i <= dots ? accent : "#eee0d2" }} />
          ))}
        </div>

        <div className="flex items-center gap-1.5 mt-auto flex-wrap">
          <span className="text-[11px] font-label-lg px-2 py-0.5 rounded-full text-tertiary" style={{ background: "#ffddb4" }}>
            +{game.xp_reward} XP
          </span>
          <span className="text-[11px] font-label-lg px-2 py-0.5 rounded-full text-primary" style={{ background: "#ffdbd0" }}>
            🪙 {game.coin_reward}
          </span>
        </div>
      </div>

      {!isComingSoon && (
        <button onClick={() => onPlay(game)}
          className="play-btn absolute bottom-4 end-4 w-11 h-11 rounded-full flex items-center justify-center text-white shadow-md active:scale-90 transition-transform"
          style={{ background: accent }} aria-label={done ? "Play again" : t("kids.games.hub.play")}>
          <span className="material-symbols-outlined icon-filled">{done ? "replay" : "arrow_forward"}</span>
        </button>
      )}
    </div>
  );
}

/* ── Decorative end-of-shelf card inviting the child to browse the full
   subject list — matches the mockup's dashed "See All" tile. ── */
function SeeAllCard({ accent, onClick }) {
  return (
    <button onClick={onClick}
      className="w-[220px] sm:w-[260px] h-[300px] sm:h-[320px] shrink-0 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 text-on-surface-variant hover:bg-surface-container-low/60 transition"
      style={{ borderColor: `${accent}55` }}>
      <span className="material-symbols-outlined text-3xl" style={{ color: accent }}>arrow_forward</span>
      <span className="font-label-lg text-sm">See All</span>
    </button>
  );
}

/* ── Loading skeleton: header bar + banner + card placeholders ── */
function HubSkeleton() {
  return (
    <div>
      <Skeleton className="h-11 w-full sm:w-[230px] mb-4" rounded="16px" />
      <Skeleton className="h-20 w-full mb-4" rounded="22px" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-3.5">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-[168px] w-full" rounded="16px" />
        ))}
      </div>
    </div>
  );
}

/* ── Empty state: friendly Cabi nudge ── */
function EmptyGamesState({ filter, onShowAll }) {
  const filtered = filter !== "all";
  return (
    <EmptyState
      mood="thinking"
      theme="light"
      title="No games yet — let's fix that!"
      subtitle={
        filtered
          ? "No games in this subject just yet. Try All Games instead!"
          : "New adventures are on the way — check back soon!"
      }
      cta={filtered ? "Explore all games" : undefined}
      onCta={filtered ? onShowAll : undefined}
    />
  );
}

/* ── Adventure Trail: a winding journey of game "stops" (fallback board) ── */
function AdventureTrail({ journey, currentIdx, onPlay, childName }) {
  if (!journey.length) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-3">🗺️</div>
        <p className="text-muted">Your adventure map is being drawn…</p>
      </div>
    );
  }

  const STEP_Y = 138;      // vertical gap between stops (px)
  const TOP    = 70;       // top padding before first stop
  const n      = journey.length;
  const height = TOP + (n - 1) * STEP_Y + 120;

  // x position (as % of width) follows a gentle S-curve
  const xPct = (i) => 50 + Math.sin(i * 0.9) * 30;   // 20%–80%
  const yPx  = (i) => TOP + i * STEP_Y;

  // Build a smooth dashed path connecting all stops (X in 0–100 viewBox units)
  const pts  = journey.map((_, i) => [xPct(i), yPx(i)]);
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const my = (y0 + y1) / 2;
    d += ` C ${x0} ${my}, ${x1} ${my}, ${x1} ${y1}`;
  }

  return (
    <div className="relative mx-auto" style={{ height, maxWidth: 420 }}>
      <style>{`
        @keyframes hop { 0%,100%{transform:translate(-50%,0)} 50%{transform:translate(-50%,-10px)} }
        @keyframes ring { 0%{box-shadow:0 0 0 0 rgba(238,95,43,.5)} 100%{box-shadow:0 0 0 18px rgba(238,95,43,0)} }
        .stop-pulse{animation:ring 1.6s ease-out infinite}
        .you-are-here{animation:hop 1.4s ease-in-out infinite}
      `}</style>

      {/* Winding dashed path */}
      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none" aria-hidden="true">
        <path d={d} fill="none" stroke="#B9CEEC" strokeWidth="6"
          strokeLinecap="round" strokeDasharray="2 5" vectorEffect="non-scaling-stroke" />
      </svg>

      {/* Stops */}
      {journey.map((g, i) => {
        const done    = isDone(g);
        const playable= isPlayable(g);
        const current = i === currentIdx;
        const locked  = !playable && !done;
        const emoji   = GAME_EMOJIS[g.slug] || "🎮";

        return (
          <div key={g.id} className="absolute" style={{ insetInlineStart: `${xPct(i)}%`, top: yPx(i), transform: "translate(-50%, -50%)" }}>
            {current && (
              <div className="you-are-here absolute start-1/2 -top-12 whitespace-nowrap" style={{ transform: "translateX(-50%)" }}>
                <div className="text-white text-[10px] font-body font-bold px-2 py-0.5 rounded-full shadow" style={{ background: "#EE5F2B" }}>YOU ARE HERE</div>
                <div className="text-2xl text-center leading-none mt-0.5">🧒</div>
              </div>
            )}

            <button
              onClick={() => playable && onPlay(g)}
              disabled={locked}
              className={`relative w-[72px] h-[72px] rounded-full flex flex-col items-center justify-center shadow-lg transition active:scale-90 ${current ? "stop-pulse" : ""}`}
              style={
                done    ? { background: "linear-gradient(150deg,#3FC08A,#22946A)" }
                : locked ? { background: "#D4DEEE" }
                : { background: "#fff", border: "5px solid #EE5F2B" }
              }>
              <span className={`text-3xl ${locked ? "grayscale opacity-60" : ""}`}>{locked ? "🔒" : emoji}</span>
              {done && <span className="absolute -top-1 -end-1 bg-white rounded-full text-success text-sm w-5 h-5 flex items-center justify-center shadow">✓</span>}
            </button>

            <div className="absolute start-1/2 top-[78px] -translate-x-1/2 w-28 text-center">
              <p className={`text-xs font-body font-bold leading-tight ${locked ? "text-faint" : "text-ink"}`}>{g.name}</p>
              {!locked && <p className="text-[10px] text-brand font-body font-semibold">+{g.xp_reward} XP {DIFF_STARS[g.difficulty] || ""}</p>}
              {locked && <p className="text-[10px] text-faint">Coming soon</p>}
            </div>
          </div>
        );
      })}

      {/* Finish flag */}
      <div className="absolute start-1/2 -translate-x-1/2 text-center" style={{ top: height - 70 }}>
        <div className="text-4xl">🏁</div>
        <p className="text-xs font-display font-semibold text-ink">Trophy Town</p>
        <p className="text-[10px] text-faint">You're doing great, {childName}!</p>
      </div>
    </div>
  );
}

// Fallback demo games when API is unavailable
const DEMO_GAMES = [
  { id:1, name:"Count & Match",      slug:"count-and-match",    subject:"math",    difficulty:"easy",   game_type:"drag_drop", xp_reward:10, coin_reward:5,  min_grade:0, max_grade:2 },
  { id:2, name:"Number Ninja",       slug:"number-ninja",       subject:"math",    difficulty:"medium", game_type:"quiz",      xp_reward:20, coin_reward:10, min_grade:2, max_grade:4 },
  { id:3, name:"Word Builder",       slug:"word-builder",       subject:"english", difficulty:"easy",   game_type:"drag_drop", xp_reward:10, coin_reward:5,  min_grade:0, max_grade:2 },
  { id:4, name:"Story Scramble",     slug:"story-scramble",     subject:"english", difficulty:"medium", game_type:"story",     xp_reward:25, coin_reward:12, min_grade:3, max_grade:6 },
  { id:5, name:"Science Explorer",   slug:"science-explorer",   subject:"science", difficulty:"medium", game_type:"quiz",      xp_reward:20, coin_reward:10, min_grade:1, max_grade:4 },
  { id:6, name:"Code Blocks Jr",     slug:"code-blocks-jr",     subject:"coding",  difficulty:"hard",   game_type:"puzzle",    xp_reward:30, coin_reward:15, min_grade:3, max_grade:6 },
  { id:7, name:"Alphabet Adventure", slug:"alphabet-adventure", subject:"english", difficulty:"easy",   game_type:"typing",    xp_reward:10, coin_reward:5,  min_grade:0, max_grade:1 },
  { id:8, name:"Math Planet",        slug:"math-planet",        subject:"math",    difficulty:"hard",   game_type:"quiz",      xp_reward:30, coin_reward:15, min_grade:4, max_grade:6 },
];
